import { useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';

// Lazy load modal components - only load when user clicks
const TermLifeModal = lazy(() => import('./TermLifeModal'));
const WholeLifeModal = lazy(() => import('./WholeLifeModal'));
const AnnuitiesModal = lazy(() => import('./AnnuitiesModal'));

export default function Services() {
  const [activeModal, setActiveModal] = useState<'term' | 'whole' | 'annuities' | null>(null);

  const services = [
    {
      icon: 'ri-shield-check-line',
      title: 'Term Life Insurance',
      description: 'Term life is the most affordable life insurance you can get. A healthy 35-year-old in Indianapolis can get $500,000 of coverage for around $28 per month. Your price is locked in when you apply — it never goes up. Perfect if you have a mortgage, young kids, or just want to protect your family while you\'re working.',
      features: ['$500K coverage from ~$28/mo in Indiana', '10, 20, or 30-year terms available', 'Rate locked at application — never increases', 'Convertible to permanent at any time'],
      guideLink: '/blog/how-much-life-insurance-indiana',
      guideLinkText: 'How much coverage do you actually need?',
      cta: 'Get Term Life Quote',
      modalType: 'term' as const,
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Whole Life Insurance',
      description: 'Whole life insurance covers you for your entire life — not just 20 or 30 years. It also builds cash value over time that you can borrow from tax-free. Many Indiana families use it to cover final expenses, plan their estate, or as a savings tool that works alongside their 401(k).',
      features: ['Lifetime coverage — never expires', 'Tax-deferred cash value growth', 'Premiums fixed for life — never increase', 'Borrow against policy tax-free'],
      guideLink: '/blog/term-vs-whole-life-indianapolis',
      guideLinkText: 'Term vs. Whole Life: which is right for you?',
      cta: 'Get Whole Life Quote',
      modalType: 'whole' as const,
    },
    {
      icon: 'ri-funds-line',
      title: 'Annuities & Retirement',
      description: 'An annuity is like a paycheck that never stops. You put in a lump sum, and it pays you back every single month — for the rest of your life. The stock market can go up or down, but your money is safe and your income keeps coming. Great for Indiana retirees who want steady income without any risk.',
      features: ['Guaranteed lifetime income stream', 'Principal 100% protected from losses', 'Tax-deferred growth — no annual limit', 'No probate — passes directly to heirs'],
      guideLink: '/blog/annuities-retirement-indianapolis',
      guideLinkText: 'How annuities fit into Indiana retirement planning',
      cta: 'Get Annuity Quote',
      modalType: 'annuities' as const,
    },
  ];

  return (
    <>
      <section id="services" className="py-20 md:py-28 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#D97642] rounded-full blur-[120px]"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#1A3A52] rounded-full blur-[140px]"></div>
        </div>

        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 bg-[#D97642]/10 text-[#D97642] text-[12px] font-semibold uppercase tracking-widest rounded-full border border-[#D97642]/20 mb-5">
              Our Services
            </span>
            <h2 className="text-[28px] sm:text-[34px] md:text-[42px] font-bold text-[#1A3A52] leading-tight">
              Life Insurance & Wealth Solutions
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] text-[#4A5568] max-w-[680px] mx-auto leading-relaxed">
              Not sure which type is right for you? That&apos;s exactly what we help with. We compare every A-rated carrier in Indiana and find the right fit for your family and budget. Most people are surprised how affordable it is.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl md:rounded-3xl border border-gray-100 hover:border-[#D97642]/30 p-8 md:p-10 transition-all duration-300 hover:shadow-[0_12px_40px_rgba(217,118,66,0.12)] relative overflow-hidden flex flex-col"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#D97642]/5 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <div className="relative flex flex-col flex-1">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#D97642] to-[#C26638] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${service.icon} text-[26px] md:text-[30px] text-white`}></i>
                  </div>

                  <h3 className="text-[20px] md:text-[24px] font-bold text-[#1A3A52] mb-3 leading-tight">
                    {service.title}
                  </h3>
                  <p className="text-[14px] md:text-[15px] text-[#4A5568] leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <ul className="space-y-3 mb-5">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center bg-[#D97642]/10 rounded-full flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-[#D97642] text-[13px]"></i>
                        </div>
                        <span className="text-[13px] md:text-[14px] text-[#4A5568] leading-relaxed">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Internal blog guide link */}
                  <Link
                    to={service.guideLink}
                    className="inline-flex items-center gap-1.5 text-[12px] md:text-[13px] font-medium text-[#D97642] hover:text-[#C26638] transition-colors mb-7 group/link cursor-pointer"
                  >
                    <i className="ri-article-line text-[14px]"></i>
                    <span className="underline underline-offset-2 decoration-[#D97642]/40 group-hover/link:decoration-[#C26638]">
                      {service.guideLinkText}
                    </span>
                    <i className="ri-arrow-right-line text-[12px] group-hover/link:translate-x-0.5 transition-transform"></i>
                  </Link>

                  <button
                    onClick={() => setActiveModal(service.modalType)}
                    aria-label={`${service.cta} — ${service.title}`}
                    className="mt-auto w-full py-3.5 bg-gradient-to-r from-[#D97642] to-[#C26638] text-white text-[14px] md:text-[15px] font-semibold rounded-xl hover:shadow-lg hover:shadow-[#D97642]/30 transition-all flex items-center justify-center gap-2 group/btn cursor-pointer whitespace-nowrap"
                  >
                    {service.cta}
                    <i className="ri-arrow-right-line text-[16px] group-hover/btn:translate-x-1 transition-transform" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Additional cross-links row */}
          <div className="mt-10 md:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 border border-gray-100 rounded-2xl p-5 md:p-6 bg-[#F7F9FB]">
            <Link to="/blog/life-insurance-for-families-indianapolis" className="flex items-center gap-3 group cursor-pointer">
              <div className="w-9 h-9 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0">
                <i className="ri-group-line text-[#D97642] text-[17px]"></i>
              </div>
              <span className="text-[13px] font-medium text-[#1A3A52] group-hover:text-[#D97642] transition-colors leading-snug">
                Best coverage for Indianapolis families
              </span>
            </Link>
            <Link to="/blog/cheapest-life-insurance-indiana" className="flex items-center gap-3 group cursor-pointer">
              <div className="w-9 h-9 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0">
                <i className="ri-price-tag-3-line text-[#D97642] text-[17px]"></i>
              </div>
              <span className="text-[13px] font-medium text-[#1A3A52] group-hover:text-[#D97642] transition-colors leading-snug">
                Cheapest life insurance in Indiana
              </span>
            </Link>
            <Link to="/blog/life-insurance-indiana-seniors" className="flex items-center gap-3 group cursor-pointer">
              <div className="w-9 h-9 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0">
                <i className="ri-user-heart-line text-[#D97642] text-[17px]"></i>
              </div>
              <span className="text-[13px] font-medium text-[#1A3A52] group-hover:text-[#D97642] transition-colors leading-snug">
                Life insurance options for Indiana seniors
              </span>
            </Link>
          </div>

          <div className="mt-10 md:mt-12 text-center">
            <p className="text-[14px] md:text-[15px] text-[#4A5568] mb-5">
              Not sure which option is right for you?
            </p>
            <a
              href="/wealth-strategy-call"
              aria-label="Book your free wealth strategy call with a local Indianapolis advisor"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-white border-2 border-[#1A3A52] text-[#1A3A52] text-[14px] md:text-[15px] font-semibold rounded-xl hover:bg-[#1A3A52] hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line text-[17px]" aria-hidden="true"></i>
              Book Your Free Wealth Strategy Call
            </a>
          </div>
        </div>
      </section>

      {/* Lazy-loaded modals */}
      {activeModal && (
        <Suspense fallback={null}>
          {activeModal === 'term' && (
            <TermLifeModal 
              isOpen={true} 
              onClose={() => setActiveModal(null)} 
            />
          )}
          {activeModal === 'whole' && (
            <WholeLifeModal 
              isOpen={true} 
              onClose={() => setActiveModal(null)} 
            />
          )}
          {activeModal === 'annuities' && (
            <AnnuitiesModal 
              isOpen={true} 
              onClose={() => setActiveModal(null)} 
            />
          )}
        </Suspense>
      )}
    </>
  );
}
